import { FieldType, FilterType, ParticipantFieldType } from '@app/api';
import React, { useState } from 'react';
import { Button } from '@app/core';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Input } from '@app/form';
import { Trans, useTranslation } from 'react-i18next';
import { FilterPopover } from 'components/ParticipationsTable';

interface Props {
  fieldRef?: string;
  fieldLabel?: string;
  fieldType?: FieldType | ParticipantFieldType;
  selectedMin?: number | Date;
  selectedMax?: number | Date;
  onFilterDataChange?: (
    field: string,
    type: FilterType,
    values: string[],
    min?: number | Date,
    max?: number | Date,
  ) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    ...theme.fonts.caption,
    padding: 0,
    height: theme.iconSize.regular,
    width: theme.iconSize.regular,
  },
  buttonIcon: {
    fontSize: theme.iconSize.large / 2,
  },
  inputContainer: {
    display: 'flex',
    gap: theme.marginBase * 2,
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    background: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.grayBorder}`,
  },
  input: {
    border: 'none',
    outline: 'none',
    textAlign: 'left',
    width: theme.marginBase * 14,
    fontSize: '1rem',
    padding: theme.marginBase,
    background: theme.colors.white,
    minHeight: theme.height.small,
    '&::-webkit-outer-spin-button': {
      display: 'none',
    },
    '&::-webkit-inner-spin-button': {
      display: 'none',
    },
    '-moz-appearance': 'textfield',
  },
  inputLabel: {
    color: theme.colors.inkTertiary,
    whiteSpace: 'nowrap',
    backgroundColor: theme.colors.grayBackground,
    borderRight: `1px solid ${theme.colors.grayBorder}`,
    padding: [[0, 12]],
    marginBottom: theme.marginBase / 4,
    display: 'flex',
    alignItems: 'center',
    height: theme.height.small,
  },
}));

//todo: Handle date pickers for date fields
export const RangeFilterButton = ({
  fieldRef,
  fieldLabel,
  fieldType,
  selectedMin,
  selectedMax,
  onFilterDataChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(
    undefined,
  );
  const [localMin, setLocalMin] = useState<number | Date | undefined>(
    selectedMin,
  );
  const [localMax, setLocalMax] = useState<number | Date | undefined>(
    selectedMax,
  );
  const hasSelection =
    selectedMin !== undefined ||
    selectedMax !== undefined ||
    localMin !== undefined ||
    localMax !== undefined;

  if (
    fieldType !== ParticipantFieldType.DATE &&
    fieldType !== ParticipantFieldType.NUMBER &&
    fieldType !== ParticipantFieldType.INCOME
  ) {
    return null;
  }

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLocalMin(selectedMin);
    setLocalMax(selectedMax);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(undefined);
  };

  const handleClearAll = () => {
    if (fieldRef && onFilterDataChange) {
      onFilterDataChange(fieldRef, FilterType.RANGE, [], undefined, undefined);
    }
    setLocalMin(undefined);
    setLocalMax(undefined);
    closePopover();
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalMin(value ? parseInt(value, 10) : undefined);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalMax(value ? parseInt(value, 10) : undefined);
  };

  const handleApply = () => {
    if (fieldRef && onFilterDataChange) {
      onFilterDataChange(fieldRef, FilterType.RANGE, [], localMin, localMax);
    }
    setLocalMin(undefined);
    setLocalMax(undefined);
    closePopover();
  };

  return (
    <>
      <Button
        variant="muted"
        color={hasSelection ? 'blue' : 'ink'}
        contentClassName={classes.button}
        xsmall
        onClick={openPopover}
      >
        <i className={classNames('ri-filter-3-line', classes.buttonIcon)} />
      </Button>
      <FilterPopover
        anchorEl={anchorEl}
        onRequestClose={closePopover}
        title={
          <Trans
            i18nKey="sessions.details.participationsDetails.rangeFilter.filterBy"
            values={{ field: fieldLabel?.toLowerCase() }}
          />
        }
        clearOnTop
        onClear={hasSelection ? handleClearAll : undefined}
        onApply={handleApply}
      >
        <div className={classes.inputContainer}>
          <div className={classes.inputGroup}>
            <div className={classes.inputLabel}>
              {t('sessions.details.participationsDetails.rangeFilter.min')}
            </div>
            <Input
              type="number"
              className={classes.input}
              value={localMin !== undefined ? String(localMin) : ''}
              onChange={handleMinChange}
              noMargin
              placeholder="Ex: 18"
            />
          </div>
          <div className={classes.inputGroup}>
            <div className={classes.inputLabel}>
              {t('sessions.details.participationsDetails.rangeFilter.max')}
            </div>
            <Input
              type="number"
              className={classes.input}
              value={localMax !== undefined ? String(localMax) : ''}
              onChange={handleMaxChange}
              noMargin
              placeholder="Ex: 45"
            />
          </div>
        </div>
      </FilterPopover>
    </>
  );
};
